import type {
  ForgotPasswordResponse,
  LoginForm,
  LoginResponse,
  LoginApiResponse,
  ResendOtpResponse,
  ResetPasswordForm,
  SignupResponse,
} from '@gokrew/models/auth';
import type { ProfileResponse } from '@gokrew/models/employee-profile';
import type {
  GetEmployeeAddressWithDetails,
  GetEmployeeEducationListResponse,
} from '@gokrew/models/employee';
import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import { Language_Options } from '@gokrew/ui/constants/language-options';

const supportedLanguageCodes = Language_Options.map((l) => l.code);

export async function login(payload: LoginForm): Promise<LoginApiResponse> {
  const { data } = await api.post<LoginApiResponse>('/auth/login', payload, {
    headers: { portal: 'employee', 'x-platform-type': 'web' },
  });
  return data;
}

export async function verifyOtp(
  otpToken: string,
  otp: string
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(
    '/auth/verify-otp',
    { otpToken, otp },
    { headers: { portal: 'employee', 'x-platform-type': 'web' } }
  );
  return data;
}

export async function resendOtp(otpToken: string): Promise<ResendOtpResponse> {
  const { data } = await api.post<ResendOtpResponse>(
    '/auth/resend-otp',
    { otpToken },
    { headers: { portal: 'employee', 'x-platform-type': 'web' } }
  );
  return data;
}

export async function signup(
  firstName: string,
  lastName: string,
  email: string,
  password: string
): Promise<SignupResponse> {
  const payload = { firstName, lastName, email, password };
  const { data } = await api.post<SignupResponse>('/auth/sign-up', payload, {
    headers: { portal: 'employee', 'x-platform-type': 'web' },
  });
  return data;
}

/**
 * Fetch the logged-in user's personal profile
 */
export async function getProfile() {
  const { data } = await api.get<ProfileResponse>('/employee/personal');
  return data;
}

/**
 * Get the logged-in user's address details
 */
export async function getAddress() {
  const { data } =
    await api.get<GetEmployeeAddressWithDetails>('/employee/address');
  return data;
}
export async function getEducation() {
  const { data } = await api.get<GetEmployeeEducationListResponse>(
    '/employee/education'
  );
  return data;
}

export async function getMe() {
  const response = await api.get('users/me');
  return response.data;
}

// Used to reset password for first time login & also later
export async function resetPassword(
  payload: ResetPasswordForm
): Promise<ApiResponse> {
  const { data } = await api.post<ApiResponse>(
    '/auth/change-password',
    payload
  );
  return data;
}

export async function sendForgotPasswordLink(
  email: string
): Promise<ForgotPasswordResponse> {
  const res = await api.post<ForgotPasswordResponse>(
    '/forgot-password',
    {
      email: email,
    },
    {
      headers: { portal: 'employee' },
    }
  );

  return res.data;
}

export async function resetForgotPassword(
  token: string,
  newPassword: string
): Promise<ForgotPasswordResponse> {
  const res = await api.post<ForgotPasswordResponse>(
    '/reset-password',
    {
      token,
      newPassword,
    },
    {
      headers: { portal: 'employee' },
    }
  );

  return res.data;
}

export async function logoutUser(): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(
    '/auth/logout',
    {},
    { headers: { portal: 'employee', 'x-platform-type': 'web' } }
  );
  return data;
}

/**
 * Update the logged-in user's profile image
 */
export async function updateEmployeeProfileImage(profileImage: string | null) {
  const { data } = await api.post('/employee/profile-update', { profileImage });
  return data;
}

/**
 * Update the logged-in user's language preference
 */
export async function updateEmployeeLanguage(language: string) {
  if (!supportedLanguageCodes.includes(language)) {
    throw new Error(`Unsupported language: ${language}`);
  }
  const { data } = await api.post('/employee/profile-update', { language });
  return data;
}
